/**
 * CW-SF-R2 TKT-6 — Forecast by Category widget (CEO role).
 *
 * Pivots the open + closed fiscal-Q opps by SF
 * ForecastCategoryName: Closed / Commit / Best Case / Pipeline /
 * Omitted. Renders a horizontal stacked-bar in canonical order.
 */
import { useCallback } from 'react';
import { apiFetch } from '@/lib/api';
import { useAutoRefresh } from '@/services/widgetRegistry';
import type { WidgetProps } from '@/types';
import { SfWidgetShell } from './SfWidgetShell';
import { buildSfWidgetUrl, formatCurrency, type SfWidgetEnvelope } from './sfWidgetUtils';

interface CategoryBucket {
  category: string;
  total: number;
  count: number;
}
interface ApiResponse extends SfWidgetEnvelope {
  categories: CategoryBucket[];
  amount_denied: boolean;
  total_visible: number;
  q_label?: string | null;
}

const REFRESH_S = 5 * 60;

export function SfForecastByCategoryWidget({ config }: WidgetProps) {
  const fetcher = useCallback(
    () => apiFetch<ApiResponse>(buildSfWidgetUrl('/mod/cw/widgets/sf/forecast-by-category', config)),
    [config],
  );
  const { data, loading, error } = useAutoRefresh<ApiResponse>(fetcher, REFRESH_S);

  const cats = data?.categories ?? [];
  const total = data?.total_visible ?? 0;

  return (
    <div className="sf-widget sf-widget--forecast">
      <SfWidgetShell
        envelope={data}
        loading={loading}
        error={error}
        hasItems={cats.length > 0}
        emptyMessage={
          data?.amount_denied
            ? "Your Salesforce profile doesn't have access to Opportunity Amount."
            : `No opportunities in ${data?.q_label ?? 'this quarter'} yet.`
        }
      >
        {data?.q_label && (
          <div className="sf-widget__header-row">
            <span className="sf-widget__quarter-label">{data.q_label}</span>
            {total > 0 && (
              <span className="sf-widget__quarter-total">{formatCurrency(total)}</span>
            )}
          </div>
        )}
        <ul className="sf-widget__bars" aria-label="Forecast by category">
          {cats.map((c) => {
            const pct = total > 0 ? (c.total / total) * 100 : 0;
            return (
              <li key={c.category} className="sf-widget__bar">
                <div className="sf-widget__bar-label">
                  <span>{c.category}</span>
                  <span className="sf-widget__bar-meta">
                    {formatCurrency(c.total)} · {c.count}
                  </span>
                </div>
                <svg
                  className="sf-widget__bar-track"
                  viewBox="0 0 100 6"
                  preserveAspectRatio="none"
                  role="img"
                  aria-label={`${c.category}: ${formatCurrency(c.total)}`}
                >
                  <rect x="0" y="0" width="100" height="6" rx="2" className="sf-widget__bar-bg" />
                  <rect x="0" y="0" width={pct} height="6" rx="2" className={`sf-widget__bar-fill sf-widget__bar-fill--fc-${c.category.toLowerCase().replace(/[^a-z]/g, '')}`} />
                </svg>
              </li>
            );
          })}
        </ul>
      </SfWidgetShell>
    </div>
  );
}
